export type Platform = "facebook" | "instagram" | "youtube";
export type PostKind = "photo" | "video";

export interface Connection {
  id: string;
  workspace_id: string;
  platform: Platform;
  provider_account_id: string;
  display_name: string;
  avatar_url: string | null;
  status: "active" | "expired" | "revoked";
  /** Which brand this account publishes as (ADR-0029). Null = unfiled. Filing
   *  only: it grants nothing and no permission check reads it. */
  brand_id: string | null;
  linked_account_id: string | null;
  metadata: Record<string, unknown>;
  scopes: string[];
  connected_at: string;
}

/** A row from `audit_events`, readable by workspace members under RLS. */
export interface AuditEvent {
  id: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface Problem {
  severity: "error" | "warning";
  code: string;
  message: string;
  fixable: "convert-jpeg" | "resize" | "compress" | null;
}

export interface ValidationResult {
  platform: Platform;
  placement: string;
  ok: boolean;
  problems: Problem[];
  sourceUrl: string;
}

export interface Delivery {
  id: string;
  post_id: string;
  platform: Platform;
  placement: string;
  status:
    | "pending" | "validating" | "uploading" | "processing"
    | "published" | "failed" | "retrying" | "skipped";
  attempt_count: number;
  provider_post_id: string | null;
  provider_url: string | null;
  provider_state: Record<string, unknown>;
  error_code: string | null;
  error_message: string | null;
  published_at: string | null;
  social_connections?: Pick<Connection, "display_name" | "avatar_url">;
}

export type YouTubePrivacy = "private" | "unlisted" | "public";

/**
 * One slide of a post, from `post_media` (migration 20260908000100).
 *
 * `media_assets` is null when the file has been deleted from the library since
 * publishing — the same thing `Post.media_assets` does, and for the same
 * reason: the record of what went out outlives the bytes.
 */
export interface PostMediaItem {
  sort_order: number;
  media_assets: Pick<
    MediaAsset,
    "id" | "storage_path" | "mime_type" | "byte_size" | "workspace_id" | "original_filename"
    | "preview_path" | "preview_bytes"
  > | null;
}

export interface Post {
  id: string;
  created_by?: string | null;
  kind: PostKind;
  caption: string;
  title: string | null;
  youtube_privacy_status: YouTubePrivacy;
  scheduled_at: string | null;
  status: "draft" | "scheduled" | "publishing" | "published" | "partially_published" | "failed";
  /** Review Post stage (20260924000100). 'pending' holds every delivery. */
  approval?: "not_required" | "pending" | "approved" | "rejected";
  review_mode?: "self" | "approver" | null;
  review_note?: string | null;
  created_at: string;
  deliveries?: Delivery[];
  /** Embedded by the queries that show a post rather than list it — the
   *  calendar draws a thumbnail, and a post with no picture is a row of text. */
  // `byte_size` and `workspace_id` are here for the egress meter (ADR-0017):
  // a thumbnail signed from a calendar row is transfer like any other, and
  // without the size on the row it can only be counted as zero.
  //
  // This is the COVER since posts became able to hold up to ten photos: it is
  // still the one file every thumbnail is drawn from, and it is still the first
  // slide. The whole set is `post_media` below.
  media_assets?: Pick<
    MediaAsset,
    "id" | "storage_path" | "mime_type" | "byte_size" | "workspace_id"
    | "preview_path" | "preview_bytes"
  >;
  /**
   * Every photo in the post, in carousel order — one entry for a single-photo
   * post, up to ten for a carousel. Comes back unordered from PostgREST, so
   * read it through `postMedia()` rather than directly.
   */
  post_media?: PostMediaItem[];
}

export const PLATFORM_LABEL: Record<Platform, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  youtube: "YouTube",
};

// ---------------------------------------------------------------- billing
//
// Plans, features and limits are rows in the database (migrations 20260822000500
// and 000600), never constants here. A new tier or a changed limit reaches the UI
// on the next load, with no deploy — so nothing in the app may hardcode a tier
// name, a price or a number.

export interface Plan {
  code: string;
  name: string;
  tagline: string | null;
  sort_order: number;
  billing_period: "none" | "month" | "year" | "lifetime";
  price_cents: number | null;
  currency: string;
  is_public: boolean;
  is_active: boolean;
}

export interface FeatureEntitlement {
  name: string;
  kind: "boolean" | "limit" | "quota";
  unit: string | null;
  /** False for a capability that is sold and modelled but not built yet. */
  implemented: boolean;
  enabled: boolean;
  /** null = unlimited. */
  limit: number | null;
  /** Current consumption for quota features; null when not metered. */
  used: number | null;
}

export interface Entitlements {
  workspace_id: string;
  plan: Plan | null;
  subscription: {
    status: string;
    current_period_end: string | null;
    cancel_at: string | null;
    started_at: string;
  } | null;
  features: Record<string, FeatureEntitlement>;
}

/** What the database sends with a PT402, so the prompt can name the limit. */
export interface PaywallDetail {
  feature: string;
  feature_name: string | null;
  reason: "not_in_plan" | "quota_exceeded" | "over_limit";
  plan: string;
  limit: number | null;
  used: number | null;
  requested: number | null;
  unit: string | null;
}

export type BillingInterval = "month" | "year" | "lifetime";

export interface PricingRegion {
  code: string;
  name: string;
  currency: string;
  countries: string[];
  is_default: boolean;
  sort_order: number;
}

export interface PlanPrice {
  plan_code: string;
  region_code: string;
  billing_interval: BillingInterval;
  currency: string;
  amount_minor: number;
}

/** Everything the pricing page needs, in one round trip (`pricing_catalog()`). */
export interface PricingCatalog {
  regions: PricingRegion[];
  plans: Plan[];
  prices: PlanPrice[];
  features: Array<{
    key: string; name: string; description: string | null; kind: string;
    unit: string | null; is_implemented: boolean; sort_order: number;
  }>;
  entitlements: Array<{
    plan_code: string; feature_key: string; enabled: boolean; limit_value: number | null;
  }>;
}

/** A row from `media_assets`, with the posts it was published in. */
/**
 * A drawer in the media library (ADR-0018). It holds no bytes and grants no
 * access — it is a label on a file, which is why `subtree_file_count` can be
 * shown to anyone who may see the library at all.
 */
export interface MediaFolder {
  id: string;
  parent_id: string | null;
  name: string;
  /** 1 for a top-level folder. */
  depth: number;
  /** "Clients / BluePeak / August shoot", for breadcrumbs and pickers. */
  path: string;
  /** Files filed directly here. */
  file_count: number;
  /** Files here and in everything below it — the number an agency asks for. */
  subtree_file_count: number;
  subtree_bytes: number;
}

export interface MediaAsset {
  id: string;
  workspace_id: string;
  storage_path: string;
  /** Null means the workspace root — "Unfiled" in the library. */
  folder_id: string | null;
  /** Which brand this file belongs to (ADR-0029). Orthogonal to `folder_id`:
   *  the folder says which shoot, the brand says whose. Null = unfiled. */
  brand_id: string | null;
  original_filename: string | null;
  mime_type: string;
  byte_size: number;
  width: number | null;
  height: number | null;
  duration_seconds: number | null;
  checksum: string | null;
  /**
   * A small copy for grids, pickers and the calendar (0017-v2 §2).
   *
   * Null on everything uploaded before derivatives existed, and on anything
   * whose thumbnail could not be made. A null here means NO SMALL RENDERING IS
   * POSSIBLE, and every such surface draws a placeholder — it must never fall
   * back to `storage_path`, which is how a 200-pixel square ends up fetching a
   * 30 MB video. Use `thumbPath` / `thumbTarget` in `api.ts`, whose nullable
   * return types enforce exactly that, rather than reading this directly.
   *
   * An operator clears the backlog from the admin portal's thumbnail queue.
   */
  preview_path: string | null;
  preview_mime: string | null;
  preview_width: number | null;
  preview_height: number | null;
  /** The derivative's own size. `byte_size` still means the original. */
  preview_bytes: number | null;
  created_at: string;
  /** Empty for an asset that was uploaded but never published. */
  posts?: Array<Pick<Post, "id" | "kind" | "caption" | "title" | "status" | "scheduled_at" | "created_at"> & {
    deliveries?: Array<Pick<Delivery, "id" | "platform" | "status" | "provider_url" | "error_message">>;
  }>;
}

// ---------------------------------------------------------------- AI studio

/** What Studio can be asked for. The same four values the database checks. */
export type DraftIntent = "post" | "description" | "punchline" | "freeform";

/** What a draft is *for*, which is not the same question as which intent wrote it. */
export type DraftKind = "caption" | "description" | "punchline" | "title";

/** Provenance only. It confers no authority and no policy reads it. */
export type DraftSource = "ai" | "manual" | "imported";

/**
 * The seam between Studio and Compose, and the only table both touch.
 *
 * `source` is why this is a content row rather than an AI row: a draft typed by
 * hand, one written by a model and one read out of a spreadsheet somebody
 * filled in from another tool are the same thing to the picker, which is what
 * makes the picker useful on a plan with no AI at all.
 */
export interface ContentDraft {
  id: string;
  workspace_id: string;
  kind: DraftKind;
  body: string;
  source: DraftSource;
  generation_id: string | null;
  media_asset_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * One named fact a brand repeats — a booking link, a phone number.
 *
 * `key` is what captions say inside `{{ }}`; `label` is what the editor calls
 * it. They are separate so renaming the label does not silently break every
 * caption that referred to the old name.
 *
 * Scoped to a brand since 20260918000300 — `labtest1`'s phone number is not
 * `sabu`'s — with a null brand meaning "shared by every brand here".
 */
export interface BrandDetail {
  id: string;
  workspace_id: string;
  /** The brand this value belongs to, or null for one shared by every brand in
   *  the workspace. See `effectiveDetails` in lib/tokens.ts. */
  brand_id: string | null;
  key: string;
  label: string;
  value: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/** A row from `ai_models`. The browser reads these only to name what wrote a draft. */
export interface AiModel {
  key: string;
  display_name: string;
  is_premium: boolean;
  sort_order: number;
}

/** The posted price of one action, from the rows the checker charges against. */
export interface AiCreditCost {
  model_key: string;
  intent: DraftIntent;
  credits: number;
}

/** skill.md. Workspace-scoped; there is no organisation-level inheritance. */
export interface AiSkill {
  id: string;
  workspace_id: string;
  name: string;
  body_md: string;
  applies_to: "all" | DraftIntent;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface AiThread {
  id: string;
  workspace_id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
}

/** One exchange. Read-only in the browser: every write comes from `ai-generate`. */
export interface AiGeneration {
  id: string;
  thread_id: string | null;
  intent: DraftIntent;
  status: "pending" | "ok" | "failed";
  billing: "plan" | "byok";
  credits: number;
  model_key: string;
  model_reported: string | null;
  input: string;
  output: string | null;
  error: string | null;
  created_at: string;
}

/** Everything about a customer's provider key except the key. */
export interface AiKeyStatus {
  workspace_id: string;
  provider: "anthropic" | "openai";
  last4: string;
  status: "unverified" | "active" | "failing" | "disabled";
  last_error: string | null;
  verified_at: string | null;
}

/** What `ai-generate` answers with. */
export interface GenerationResult {
  generationId: string;
  threadId: string;
  text: string;
  credits: number;
  /** null when the plan is unlimited; unchanged when the workspace brought its own key. */
  creditsLeft: number | null;
  creditsCharged: boolean;
  model: string;
  modelKey: string;
  /** The model ran out of room. The text is usable but ends mid-thought. */
  truncated: boolean;
  knowledgeUsed: string[];
  /** Files that matched but did not fit the budget. Named, never dropped in silence. */
  knowledgeOmitted: string[];
}
